'use client'

import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react"
import { Field, Form, Formik } from "formik"
import { useMemo, useState } from "react"
import * as Yup from 'yup'
import { BriefcaseIcon } from "@heroicons/react/16/solid"
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { AssignFormFieldType } from "../utils/types"
import clsx from "clsx"
import { IconTitleButton } from "./icon-title-button"
import Spinner from "./spinner"
import { request } from "../utils/request"
import { api } from "../utils/api"
import AddressSearch from "./address-search-box"

interface AssignFormProps {
    address?: string;
    id: number;
    coords?: google.maps.LatLngLiteral
}

export default function AssignForm(props: AssignFormProps) {
    const [visible, setVisible] = useState(false)
    const open = () => {
        setVisible(true)
    }
    const initialValues: AssignFormFieldType = useMemo(() => ({ customer_address_latitude: 0, customer_address_longitude: 0, customer_address: '', job_issue: '', customer_contact_number: '', customer_name: '' }), []);

    const close = () => {
        setVisible(false)
    }

    return (
        <>
            <IconTitleButton icon={BriefcaseIcon} onClick={open} title="Assign Job" />
            <Dialog open={visible} onClose={close} className="relative z-10">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-900 opacity-25 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                />
                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        >
                            <h2 className="mt-5 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
                                Assign Job
                            </h2>
                            <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-5 lg:px-8">
                                <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={async (values) => {
                                            values.customer_contact_number = parsePhoneNumberFromString(values.customer_contact_number, { defaultCountry: 'IN', extract: true })?.formatInternational() ?? values.customer_contact_number;
                                            const body = {
                                                locksmith_id: props.id,
                                                customer_name: values.customer_name,
                                                customer_address: values.customer_address,
                                                customer_address_latitude: values.customer_address_latitude,
                                                customer_address_longitude: values.customer_address_longitude,
                                                customer_contact_number: values.customer_contact_number,
                                                job_issue: values.job_issue
                                            }
                                            try {
                                                await request('POST', api.locksmith.job, body)
                                                close()
                                            } catch (error) {
                                                console.log("🚀 ~ onSubmit={ ~ error:", error)
                                            }
                                        }}
                                    >
                                        {helpers => (
                                            <Form>
                                                <label htmlFor="customer_name" className={
                                                    clsx("text-sm font-medium leading-6",
                                                        { 'text-red-600': helpers.errors.customer_name },
                                                        { 'text-gray-900': !helpers.errors.customer_name })
                                                }>
                                                    Customer name<label className="text-sm font-large leading-6 text-red-500">{helpers.errors.customer_name && '*'}</label>
                                                </label>
                                                <Field
                                                    className="block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                                    placeholder="e.g. John Joe"
                                                    name="customer_name" />
                                                <div className='mt-2' />
                                                <label htmlFor="customer_contact_number" className={
                                                    clsx("text-sm font-medium leading-6",
                                                        { 'text-red-600': helpers.errors.customer_contact_number },
                                                        { 'text-gray-900': !helpers.errors.customer_contact_number })
                                                }>
                                                    Customer Number<label className="text-sm font-large leading-6 text-red-500">{helpers.errors.customer_contact_number && '*'}</label>
                                                </label>
                                                <Field
                                                    className="block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                                    placeholder="+12162222222"
                                                    name="customer_contact_number" />
                                                <div className='mt-2' />
                                                <label htmlFor="customer_contact_number" className={
                                                    clsx("text-sm font-medium leading-6",
                                                        { 'text-red-600': helpers.errors.customer_address },
                                                        { 'text-gray-900': !helpers.errors.customer_address })
                                                }>
                                                    Customer Address<label className="text-sm font-large leading-6 text-red-500">{helpers.errors.customer_address && '*'}</label>
                                                </label>
                                                {/* <textarea
                                                    rows={4}
                                                    className="block resize-none w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                                                    value={props.address}
                                                    disabled
                                                    name="customer_address" /> */}
                                                <AddressSearch onChangeLatLong={(values) => {
                                                    helpers.setFieldValue('customer_address_longitude', values?.coords?.longitude)
                                                    helpers.setFieldValue('customer_address_latitude', values?.coords?.latitude)
                                                    helpers.setFieldValue('customer_address', values?.address_name)
                                                }} />
                                                {/* <Field
                                                    as={'textarea'}
                                                    rows={4}
                                                    placeholder="Customer address"
                                                    className="block resize-none w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                                                    name="customer_address" /> */}
                                                <div className='mt-2' />
                                                <label htmlFor="customer_contact_number" className={
                                                    clsx("text-sm font-medium leading-6",
                                                        { 'text-red-600': helpers.errors.job_issue },
                                                        { 'text-gray-900': !helpers.errors.job_issue })
                                                }>
                                                    Issue<label className="text-sm font-large leading-6 text-red-500">{helpers.errors.job_issue && '*'}</label>
                                                </label>
                                                <Field
                                                    as={'textarea'}
                                                    rows={4}
                                                    placeholder="Describe issue..."
                                                    className="block resize-none w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                                                    name="job_issue" />
                                                {
                                                    !helpers.isSubmitting ?
                                                        <button
                                                            className="mt-5 flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                                            type="submit" disabled={helpers.isSubmitting}>
                                                            Submit
                                                        </button> : <div className="flex justify-center items-center">
                                                            <Spinner />
                                                        </div>
                                                }
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div >
            </Dialog>
        </>
    )
}

const validationSchema = Yup.object().shape({
    customer_contact_number: Yup.string().required('Required!').test('is-valid-phone', 'Please enter a valid phone number', (value) => {
        if (!value) return false;
        const regex = /^[^a-zA-Z]*$/;
        const phoneNumber = parsePhoneNumberFromString(value, { defaultCountry: 'IN', extract: true });
        return phoneNumber
            ? phoneNumber.isValid() ? regex.test(value) : false
            : false;
    }),
    customer_name: Yup.string().required('Required!'),
    job_issue: Yup.string().required('Required!'),
    customer_address: Yup.string().required('Required!'),
    customer_address_latitude: Yup.number().required('Required!'),
    customer_address_longitude: Yup.number().required('Required!'),
})
